import { styled } from '@linaria/react';
import { Box, Stack, Text } from '@mantine/core';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { LineChartLegendItemWithCheckbox } from '../../../../components/line-chart/line-chart-legend-item-with-checkbox';
import { ChartType } from '../../../../store/financials-store/financials-store.type';
import { baseWhite, inkLight } from '../../../../styles/design-tokens';
import { DEFAULT_CHART_COLORS } from '../../../financials/constants';
import type { MeasureMetadata, WorkPeriod } from '../../work-periods.type';
import { getLastValue, getUnitsLabel, sortWorkPeriodsByStartDate } from '../helpers/measure-comparison-chart.helpers';

const CustomTooltip = (props: any) => {
  const { t } = useTranslation();
  const { active, payload, label, customMeasures } = props as any;
  if (active && payload && payload.length) {
    return (
      <Box bg={baseWhite} p={10} style={{ border: `1px solid ${inkLight}` }}>
        <Stack gap={4}>
          <Text>{label}</Text>
          {payload
            .filter((entry: any) => !entry.dataKey.includes('_target'))
            .map((entry: any) => {
              const measure = customMeasures.find((m: any) => m.measure_name === entry.dataKey);
              const value = entry.value.toFixed(measure?.measure_units === 'Percentage' ? 1 : 0);
              const unit = getUnitsLabel(measure?.measure_units, t);
              const color = entry.color;

              return (
                <Text key={String(entry.dataKey)} c={color}>
                  {`${measure?.measure_title || entry.dataKey}: ${value} ${unit}`}
                </Text>
              );
            })}
        </Stack>
      </Box>
    );
  }
  return null;
};

const CustomLegend = ({
  selectedMeasures,
  visibleMeasures,
  onMeasureToggle,
}: {
  selectedMeasures: MeasureMetadata[];
  visibleMeasures: Record<string, boolean>;
  onMeasureToggle: (measureName: string) => void;
}) => {
  return (
    <LegendContainer>
      {selectedMeasures.map((measure, index) => {
        const color = DEFAULT_CHART_COLORS[index % DEFAULT_CHART_COLORS.length];
        const label = measure?.measure_title || measure.measure_name;

        return (
          <LineChartLegendItemWithCheckbox
            key={measure.measure_name}
            value={measure.measure_name}
            label={label}
            color={color}
            isChecked={visibleMeasures[measure.measure_name] ?? true}
            onToggle={() => onMeasureToggle(measure.measure_name)}
          />
        );
      })}
    </LegendContainer>
  );
};

interface MeasureComparisonChartProps {
  customMeasures: MeasureMetadata[];
  measureData: Record<string, any>;
  workPeriods: WorkPeriod[];
  chartType: ChartType;
  selectedMeasures: MeasureMetadata[];
  visibleMeasures: Record<string, boolean>;
  onMeasureToggle: (measureName: string) => void;
  onMeasureClick: (measureName: string, workPeriodName: string, title: string, date?: string | null) => void;
}

interface ChartDataPoint {
  name: string;
  isForecast?: boolean;
  [key: string]: any;
}

export const MeasureComparisonChart = ({
  customMeasures,
  measureData,
  chartType,
  workPeriods,
  selectedMeasures,
  visibleMeasures,
  onMeasureToggle,
  onMeasureClick,
}: MeasureComparisonChartProps) => {
  const chartRef = useRef<HTMLDivElement>(null!);

  // Filter visible measures
  const visibleSelectedMeasures = selectedMeasures.filter((measure) => visibleMeasures[measure.measure_name] ?? true);

  const chartData: ChartDataPoint[] = [];

  // Sort work periods by start date (oldest to newest)
  const sortedWorkPeriods = sortWorkPeriodsByStartDate(workPeriods);

  // Group measures by work period
  sortedWorkPeriods.forEach((workPeriod) => {
    const dataPoint: ChartDataPoint = {
      name: workPeriod.name,
      workPeriodId: workPeriod.id,
    };

    // Add values for each selected measure
    selectedMeasures.forEach((measure) => {
      const measureValues = measureData[workPeriod.id]?.[measure.measure_name];
      if (measureValues) {
        const lastValue = getLastValue(measureValues);
        dataPoint[measure.measure_name] = lastValue !== null ? lastValue : null;
      } else {
        dataPoint[measure.measure_name] = null;
      }
    });

    chartData.push(dataPoint);
  });

  return (
    <ChartWrapper>
      {chartData.length > 0 && selectedMeasures.length > 0 ? (
        <ChartContainer ref={chartRef}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={chartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 70 }}
              barGap={10}
              barCategoryGap={30}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="name"
                textAnchor="middle"
                tick={{ fontSize: 12, textAnchor: 'middle' }}
                interval={0}
                tickLine
                axisLine
              />
              <YAxis
                tick={{ fontSize: 12 }}
                tickFormatter={(value: number) => {
                  return value >= 1000 ? `${value / 1000}k` : `${value}`;
                }}
              />
              <Tooltip
                content={<CustomTooltip active payload={undefined} label={undefined} customMeasures={customMeasures} />}
              />
              <Legend
                content={
                  <CustomLegend
                    selectedMeasures={selectedMeasures}
                    visibleMeasures={visibleMeasures}
                    onMeasureToggle={onMeasureToggle}
                  />
                }
              />

              {visibleSelectedMeasures.map((measure) => {
                const color = DEFAULT_CHART_COLORS[selectedMeasures.indexOf(measure) % DEFAULT_CHART_COLORS.length];

                switch (chartType) {
                  case ChartType.Bar:
                    return (
                      <Bar
                        key={measure.measure_name}
                        dataKey={measure.measure_name}
                        name={measure?.measure_title || measure.measure_name}
                        fill={color}
                        style={{ cursor: 'pointer' }}
                        onClick={(data: any) => {
                          if (data && data.payload) {
                            onMeasureClick(
                              measure.measure_name,
                              data.payload.name,
                              measure?.measure_title || measure.measure_name,
                            );
                          }
                        }}
                      ></Bar>
                    );
                  case ChartType.Line:
                    return (
                      <Line
                        key={measure.measure_name}
                        dataKey={measure.measure_name}
                        name={measure?.measure_title || measure.measure_name}
                        stroke={color}
                        strokeWidth={2}
                        dot={{ fill: color, strokeWidth: 2, r: 4 }}
                        activeDot={(props: any) => {
                          const { cx, cy, payload } = props;
                          return (
                            <circle
                              cx={cx}
                              cy={cy}
                              r={6}
                              fill={color}
                              stroke={color}
                              strokeWidth={2}
                              style={{ cursor: 'pointer' }}
                              onClick={() => {
                                if (payload) {
                                  onMeasureClick(
                                    measure.measure_name,
                                    payload.name,
                                    measure?.measure_title || measure.measure_name,
                                  );
                                }
                              }}
                            />
                          );
                        }}
                      ></Line>
                    );
                  default:
                    return null;
                }
              })}
            </ComposedChart>
          </ResponsiveContainer>
        </ChartContainer>
      ) : (
        <EmptyState>
          <Text>Select at least one measure and work period to view the chart.</Text>
        </EmptyState>
      )}
    </ChartWrapper>
  );
};

const ChartWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
`;

const ChartContainer = styled.div`
  flex: 1;
  min-height: 300px;
  width: 100%;

  svg {
    border: none !important;
    outline: none !important;
  }
`;

const LegendContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, max-content));
  gap: 12px;
  justify-content: center;
  align-items: center;
  width: 100%;
  max-width: 100%;

  & > * {
    justify-self: center;
  }
`;

const EmptyState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  text-align: center;
`;
